import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight, Landmark, Upload, FileCheck2, X } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = { first_name: "", last_name: "", email: "", phone: "", university: "", study_year: "", motivation: "" };
const benefits = [
  "Toegang tot alle events en workshops",
  "Internationale stagemogelijkheden via STEP",
  "Netwerk van advocaten en medestudenten",
  "Deelname aan moot court competities",
];
const ACCEPTED = ["image/jpeg", "image/png", "application/pdf"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function LidWorden() {
  const [form, setForm] = useState(empty);
  const [proof, setProof] = useState(null);
  const [proofError, setProofError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setProof(null);
      return;
    }
    if (!ACCEPTED.includes(f.type)) {
      setProofError("Enkel jpg, png of pdf toegestaan.");
      setProof(null);
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      setProofError("Het bestand mag maximaal 5 MB zijn.");
      setProof(null);
      e.target.value = "";
      return;
    }
    setProofError("");
    setProof(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!proof) {
      setProofError("Laad een bewijs van betaling op om je aanvraag te versturen.");
      toast.error("Bewijs van betaling ontbreekt.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("payment_proof", proof);
      await api.post("/members", fd);
      setDone(true);
      toast.success("Je aanvraag is verzonden!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="lid-worden-page">
      <PageHero
        eyebrow="Sluit je aan"
        title={<>Word lid van<br />ELSA Brussel.</>}
        subtitle="Vul het formulier in en word deel van de grootste onafhankelijke studentenorganisatie voor rechtenstudenten ter wereld."
        testid="join-hero"
      />
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <h2 className="font-serif text-3xl text-[#0B0B0C] mb-6">Waarom lid worden?</h2>
          <ul className="space-y-4">
            {benefits.map((b) => (
              <li key={b} className="flex gap-3 text-[#0B0B0C]">
                <CheckCircle2 size={20} className="mt-0.5 shrink-0" style={{ color: "var(--elsa-blue)" }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-[#0B0B0C]/50 border-l-2 pl-3" style={{ borderColor: "var(--elsa-blue)" }}>
            Je lidmaatschap is geldig voor het volledige academiejaar 2026-2027 (1 jaar).
          </p>
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="border border-black/10 bg-white p-12 text-center"
              data-testid="join-success"
            >
              <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: "var(--elsa-blue)" }} />
              <h3 className="font-serif text-3xl text-[#0B0B0C] mb-2">Bedankt, {form.first_name}!</h3>
              <p className="text-muted-foreground">We hebben je aanvraag ontvangen en nemen snel contact met je op.</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="border border-black/10 bg-white p-8 md:p-10 space-y-5" data-testid="join-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="first_name">Voornaam *</Label>
                  <Input id="first_name" data-testid="join-first-name" required value={form.first_name} onChange={set("first_name")} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="last_name">Achternaam *</Label>
                  <Input id="last_name" data-testid="join-last-name" required value={form.last_name} onChange={set("last_name")} className="mt-1.5" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="email">E-mail *</Label>
                  <Input id="email" type="email" data-testid="join-email" required value={form.email} onChange={set("email")} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="phone">Telefoon *</Label>
                  <Input id="phone" data-testid="join-phone" required value={form.phone} onChange={set("phone")} className="mt-1.5" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="university">Universiteit *</Label>
                  <select id="university" data-testid="join-university" required value={form.university} onChange={set("university")} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">Selecteer...</option>
                    <option value="VUB">VUB</option>
                    <option value="Campus Brussel KUL">Campus Brussel KUL</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="study_year">Studiejaar *</Label>
                  <select id="study_year" data-testid="join-year" required value={form.study_year} onChange={set("study_year")} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">Selecteer...</option>
                    <option>1e Bachelor</option>
                    <option>2e Bachelor</option>
                    <option>3e Bachelor</option>
                    <option>1e Master</option>
                    <option>2e Master</option>
                    <option>Andere</option>
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="motivation">Waarom wil je lid worden?</Label>
                <Textarea id="motivation" data-testid="join-motivation" rows={4} value={form.motivation} onChange={set("motivation")} className="mt-1.5" />
              </div>

              {/* BETAALINSTRUCTIE + BEWIJS UPLOAD */}
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F5F7FB] p-5" data-testid="payment-section">
                <div className="flex gap-3">
                  <Landmark size={22} className="mt-0.5 shrink-0" style={{ color: "var(--elsa-blue)" }} />
                  <div className="text-sm text-[#0B0B0C]/80 leading-relaxed">
                    Om je lidmaatschap te activeren, stort je <b>5 euro</b> op rekeningnummer <b>BE92 7360 2178 2123</b> (op naam van <b>ELSA BRUSSEL VZW</b>) met vermelding van je naam. Laad hieronder een bewijs van betaling op.
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Bewijs van betaling * <span className="font-400 text-[#0B0B0C]/50">(jpg, png of pdf — max 5 MB)</span></Label>
                  {proof ? (
                    <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-[#E2E8F0] bg-white px-4 py-3" data-testid="join-proof-selected">
                      <span className="flex items-center gap-2 text-sm text-[#0B0B0C] truncate">
                        <FileCheck2 size={16} style={{ color: "var(--elsa-blue)" }} />
                        <span className="truncate">{proof.name}</span>
                      </span>
                      <button type="button" onClick={() => setProof(null)} data-testid="join-proof-remove" className="shrink-0 h-7 w-7 grid place-items-center rounded-md border border-[#E2E8F0] hover:border-red-500 hover:text-red-500"><X size={14} /></button>
                    </div>
                  ) : (
                    <label className="mt-2 flex items-center gap-2 rounded-md border border-dashed border-[#C7CDD9] bg-white px-4 py-3 text-sm text-[#0B0B0C]/70 cursor-pointer hover:border-[#0A3087] hover:text-[#0A3087] transition-colors" data-testid="join-proof-label">
                      <Upload size={16} />
                      Kies een bestand...
                      <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={onFile} className="hidden" data-testid="join-proof-input" />
                    </label>
                  )}
                  {proofError && <p className="mt-2 text-sm text-red-600" data-testid="join-proof-error">{proofError}</p>}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="join-submit"
                className="group inline-flex items-center gap-2 text-sm font-600 px-7 py-3.5 rounded-full text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{ background: "var(--elsa-blue)" }}
              >
                {loading ? "Bezig..." : "Aanvraag versturen"}
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
